import cloneDeep from 'lodash/fp/cloneDeep';
import userService from '../service/userService';
import businessService from '../service/businessService';
import userRoles from '@/utils/enums/userRoles';
import variants from '@/utils/enums/alertVariants';
import router from '../router';
import i18n from '../i18n';
import log from '../service/log';
import { setLocale, getLocale } from '@/utils/i18nHelpers';

const initialState = {
  isAuthenticated: null,
  currentUser: null,
  currentBusinessUserRole: null,
};

const getters = {
  displayName: state => {
    if (state.currentUser) {
      if (state.currentUser.firstName && state.currentUser.lastName) {
        return `${state.currentUser.firstName} ${state.currentUser.lastName}`;
      }
      return state.currentUser.email;
    }
    return '';
  },
  createEditRoles: state => {
    if (state.currentBusinessUserRole === userRoles.SUPPORT) {
      return [
        {
          value: userRoles.SUPPORT,
          label: 'roles.support',
        },
      ];
    }

    if (state.currentBusinessUserRole === userRoles.SALES) {
      return [
        {
          value: userRoles.SUPPORT,
          label: 'roles.support',
        },
        {
          value: userRoles.SALES,
          label: 'roles.sales',
        },
      ];
    }

    const roles = [
      {
        value: userRoles.ADMIN,
        label: 'roles.admin',
      },
      {
        value: userRoles.FINANCE,
        label: 'roles.finance',
      },
      {
        value: userRoles.SUPPORT,
        label: 'roles.support',
      },
      {
        value: userRoles.SALES,
        label: 'roles.sales',
      },
    ];

    if (state.currentBusinessUserRole === userRoles.AV_ADMIN) {
      roles.push({
        value: userRoles.AV_ADMIN,
        label: 'roles.av_admin',
      });
    }

    return roles;
  },
};

const actions = {
  // Example payload = { auth: this.$auth, redirect: 'www.test.ca'}
  // eslint-disable-next-line no-unused-vars
  async logout({ commit }, payload) {
    // Log them out of av auth too to get rid of session data
    await userService.logout();
    payload.auth.logout({
      returnTo: payload.redirect,
    });
  },

  // My profile update
  async updateUserData({ commit }, payload) {
    try {
      commit('SET_LOADING', true, { root: true });
      await userService.updateUserData(payload);
      const userData = cloneDeep(payload);
      setLocale(userData.language);
      commit('SET_USER_DATA', userData);
      commit(
        'SET_MESSAGE',
        { variant: variants.SUCCESS, message: { msg1: i18n.global.t('editProfilePage.updateSuccess') } },
        { root: true }
      );
    } catch (e) {
      commit(
        'SET_MESSAGE',
        { variant: variants.DANGER, message: { msg1: i18n.global.t('editProfilePage.updateError') } },
        { root: true }
      );
      log(e);
    } finally {
      commit('SET_LOADING', false, { root: true });
    }
  },

  // User data update
  async updateUserRole({ commit }, payload) {
    try {
      commit('SET_LOADING', true, { root: true });
      await userService.updateUserRole(payload);
      router.push('/users').catch(() => {});
      commit(
        'SET_MESSAGE',
        { variant: variants.SUCCESS, message: { msg1: i18n.global.t('editProfilePage.updateSuccess') } },
        { root: true }
      );
    } catch (e) {
      commit(
        'SET_MESSAGE',
        { variant: variants.DANGER, message: { msg1: i18n.global.t('editProfilePage.updateError') } },
        { root: true }
      );
      log(e);
    } finally {
      commit('SET_LOADING', false, { root: true });
    }
  },

  async fetchAndSetUser({ commit }) {
    const userResponse = await userService.fetchUser();
    const userData = userResponse.data;
    setLocale(userData.language);
    commit('SET_USER_DATA', userData);

    // Handle Intercom
    if (window.Intercom && userData.intercomToken) {
      try {
        // Boot with JWT for full messenger functionality
        const bootOptions = {
          app_id: window.envVars?.VUE_APP_INTERCOM_APP_ID,
          intercom_user_jwt: userData.intercomToken,
          current_product: 'av_dashboard',
          current_language: userData.language,
          hide_default_launcher: false,
        };

        window.Intercom('boot', bootOptions);
      } catch (error) {
        log('Failed to configure Intercom:', error);
      }
    }
  },

  async fetchAndSetBusinesses({ commit }) {
    const businessesResponse = await userService.fetchBusinesses();
    const { businesses } = businessesResponse.data;
    businesses.sort((a, b) => a.name.localeCompare(b.name));
    commit('business/SET_BUSINESS_DATA', businesses, { root: true });
  },

  async fetchAndSetCurrentBusinessFromSession({ commit }) {
    const businessResponse = await businessService.getBusinessNoId();
    commit('business/SET_CURRENT_BUSINESS', businessResponse.data, { root: true });
    commit('SET_CURRENT_BUSINESS_USER_ROLE', businessResponse.data.role);
  },

  // Set the first business as the current business
  async setDefaultBusiness({ commit, rootState }) {
    const businessRes = await businessService.getBusinessWithId(rootState.business.userBusinesses[0].id);
    commit('business/SET_CURRENT_BUSINESS', businessRes.data, { root: true });
    commit('SET_CURRENT_BUSINESS_USER_ROLE', businessRes.data.role);
  },

  // eslint-disable-next-line no-unused-vars
  async redirectToLogin({ commit }, $auth) {
    $auth.loginWithRedirect({
      ui_locales: getLocale().replace('_', '-'),
    });
  },
};

const mutations = {
  AUTH_STATUS: (state, value) => {
    state.isAuthenticated = value;
  },

  SET_USER_DATA: (state, userData) => {
    state.currentUser = userData;
    if (typeof window !== 'undefined' && window.Intercom) {
      try {
        window.Intercom('update', {
          current_language: userData.language,
        });
      } catch (error) {
        log('Failed to update Intercom:', error);
      }
    }
  },

  SET_BUSINESS_SELECTED: (state, value) => {
    state.businessSelected = value;
  },

  RESET_STATE: state => {
    const init = initialState;
    Object.keys(init).forEach(key => {
      state[key] = init[key];
    });
  },

  SET_CURRENT_BUSINESS_USER_ROLE: (state, role) => {
    state.currentBusinessUserRole = role;
  },
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  getters,
  actions,
  mutations,
};
